import React, { useMemo, useState } from "react";
import _ from "lodash";
import classNames from "classnames";
import { Button, ProgressIndicator, Tooltip } from "@toddle-edu/ds-web";
import { colors } from "@toddle-edu/ds-theme";
import {
  ChevronDownOutlined,
  ExpandArrowsTwoOutlined,
  ReloadArrowOutlined,
} from "@toddle-edu/ds-icons";
import { connect } from "react-redux";

import { getSyncJobStatusConfig } from "Integrations/modules/IntegrationsUtils";
import { retryTemporalWorkflow } from "Workflows/modules/WorkflowsModule";
import { getJobFeedQuery } from "Integrations/modules/IntegrationsQueries";
import { navigateToWorkflow } from "modules/NavigationModule";

import AnimateHeight from "UIComponents/AnimateHeight";
import LoadingDots from "UIComponents/LoadingDots";

import { useI18n } from "Hooks";
import { onEnterOrSpaceKeyDown } from "Utils";

import classes from "./WorkflowItem.scss";
import LearningCourseDiffSyncStatus from "../LearningCourseDiffSyncStatus";

const LEARNING_COURSE_DIFF_SYNC_V3 = "LEARNING_COURSE_DIFF_SYNC_V3";

const SYNC_FLOW_ENTITY_TYPES = [
  "LEARNING_COURSE_DIFF_SYNC",
  LEARNING_COURSE_DIFF_SYNC_V3,
];

const TitleWithTooltip = ({ title, children }) => (
  <Tooltip tooltip={title} placement="top" zIndex={999999}>
    {children}
  </Tooltip>
);

const EntityComponent = props => {
  const {
    textColor,
    isLatest,
    retryAllowed,
    numOfRetriesAllowed,
    status,
    handleRetryJob,
    id,
    statusLabel,
    entityType,
    workflowSubTaskMetadata,
  } = props;

  switch (entityType) {
    case "LEARNING_COURSE_DIFF_SYNC":
    case LEARNING_COURSE_DIFF_SYNC_V3: {
      return (
        <LearningCourseDiffSyncStatus
          workflowSubTaskMetadata={workflowSubTaskMetadata}
          statusLabel={statusLabel}
        />
      );
    }
    default: {
      return (
        <Button
          dsVersion="2.0"
          type="inline"
          style={{ color: `var(${textColor})` }}
          size="small"
          rightIcon={
            isLatest &&
            status === "FAILED" &&
            retryAllowed &&
            numOfRetriesAllowed > 0 ? (
              <ReloadArrowOutlined onClick={handleRetryJob(id)} />
            ) : null
          }
        >
          {statusLabel}
        </Button>
      );
    }
  }
};

const WorkflowSteps = ({
  t,
  expandedJobId,
  updateExpandedJobId,
  jobId,
  subtasks,
  totalCount,
  completedCount,
  loadingState,
  handleRetryJob,
  handleRedirectClick,
  isLatest,
}) => {
  const isExpanded = expandedJobId === jobId;
  const isSyncFlowJob = useMemo(
    () =>
      subtasks.some(({ entityType }) =>
        _.includes(SYNC_FLOW_ENTITY_TYPES, entityType)
      ),
    [subtasks]
  );

  const toggleIsExpanded = () => updateExpandedJobId(jobId);

  return (
    <div className={classes.stepsContainer}>
      <div
        className={classes.headerContainer}
        onClick={toggleIsExpanded}
        onKeyDown={e => onEnterOrSpaceKeyDown(e, toggleIsExpanded)}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={t("workflows:m_of_n_completed", {
          completedCount,
          totalCount,
        })}
      >
        <div className={classNames("text-body-s", classes.titleContainer)}>
          {t("workflows:m_of_n_completed", {
            completedCount,
            totalCount,
          })}
        </div>
        <div className={classes.rightSection}>
          {!isSyncFlowJob && (
            <ExpandArrowsTwoOutlined
              variant="subtle"
              size="xxx-small"
              onClick={handleRedirectClick(jobId)}
            />
          )}

          <ChevronDownOutlined
            variant="subtle"
            size="xxx-small"
            className={classNames(classes.arrowContainer, {
              [classes.openArrow]: isExpanded,
              [classes.closeArrow]: !isExpanded,
            })}
          />
        </div>
      </div>
      <AnimateHeight duration={300} height={!isExpanded ? 0 : "auto"}>
        <div className={classes.scrollContainer}>
          {subtasks.map(
            ({
              id,
              title,
              status,
              retryAllowed,
              numOfRetriesAllowed,
              workflowSubTaskMetadata = {},
              entityType,
            }) => {
              const { label: statusLabel, textColor } = getSyncJobStatusConfig({
                status,
                t,
              });

              return (
                <div className={classes.workflowRow} key={id}>
                  <TitleWithTooltip title={title}>
                    <div
                      className={classNames(
                        "text-body-s",
                        classes.titleContainer
                      )}
                    >
                      {title}
                    </div>
                  </TitleWithTooltip>
                  <div
                    className={classNames(classes.workflowStatus, {
                      [classes.workflowStatusForSync]: _.isEqual(
                        entityType,
                        LEARNING_COURSE_DIFF_SYNC_V3
                      ),
                      "text-body-s": _.isEqual(
                        entityType,
                        LEARNING_COURSE_DIFF_SYNC_V3
                      ),
                    })}
                  >
                    {loadingState[id] ? (
                      <LoadingDots />
                    ) : (
                      <EntityComponent
                        textColor={textColor}
                        isLatest={isLatest}
                        retryAllowed={retryAllowed}
                        numOfRetriesAllowed={numOfRetriesAllowed}
                        status={status}
                        handleRetryJob={handleRetryJob}
                        id={id}
                        statusLabel={statusLabel}
                        entityType={entityType}
                        workflowSubTaskMetadata={workflowSubTaskMetadata}
                      />
                    )}
                  </div>
                </div>
              );
            }
          )}
        </div>
      </AnimateHeight>
    </div>
  );
};

const WorkflowItem = ({
  item = {},
  expandedJobId,
  updateExpandedJobId,
  retryTemporalWorkflow,
  jobFeedVariables,
  navigateToWorkflow,
}) => {
  const {
    id,
    title,
    subtasks,
    totalCount,
    completedCount,
    failedCount,
    isLatest = false,
  } = item;

  const { t } = useI18n(["common", "workflows"]);

  const [loadingState, setLoadingState] = useState({});

  const handleRetryJob = subtaskId => async e => {
    e?.stopPropagation();
    setLoadingState(prevLoadingState => {
      return {
        ...prevLoadingState,
        [subtaskId]: true,
      };
    });

    await retryTemporalWorkflow({
      variables: {
        input: {
          subTaskIds: [subtaskId],
        },
      },
      refetchQueries: [
        {
          query: getJobFeedQuery,
          variables: jobFeedVariables,
        },
      ],
    });

    setLoadingState(prevLoadingState => {
      return {
        ...prevLoadingState,
        [subtaskId]: false,
      };
    });
  };

  const handleRedirectClick = jobId => e => {
    e.stopPropagation();
    navigateToWorkflow({
      workflowId: jobId,
    });
  };

  return (
    <div className={classes.container} id="my_tooltip">
      <div className={classes.topContainer}>
        <TitleWithTooltip title={title}>
          <div className={classNames("text-label", classes.titleContainer)}>
            {title}
          </div>
        </TitleWithTooltip>
        <ProgressIndicator
          variant="progress-bar"
          showTooltipArrow
          containerStyle={{ width: "100%" }}
          segmentedProgress={[
            {
              id: "SUCCESS",
              color: colors.surfaceOnSuccess,
              progress: Math.round((completedCount * 100) / totalCount),
              tooltip: "Completed",
            },
            {
              id: "FAILURE",
              color: colors.surfaceOnError,
              progress: Math.round((failedCount * 100) / totalCount),
              tooltip: "Failed",
            },
          ]}
        />
      </div>
      <WorkflowSteps
        subtasks={subtasks}
        jobId={id}
        t={t}
        expandedJobId={expandedJobId}
        updateExpandedJobId={updateExpandedJobId}
        totalCount={totalCount}
        completedCount={completedCount}
        loadingState={loadingState}
        handleRetryJob={handleRetryJob}
        handleRedirectClick={handleRedirectClick}
        isLatest={isLatest}
      />
    </div>
  );
};

const mapActionCreators = {
  retryTemporalWorkflow,
  navigateToWorkflow,
};

export default connect(null, mapActionCreators)(WorkflowItem);
